import type { ZudokuConfig } from "zudoku";
import getCodeSnippet from "./codeSnippets";

// //
// monkey patch fetch to change the url to cors-enabled backend.authorix.com
// because api.authorix.com does not have CORS enabled, and is only used for server-side requests. This allows the frontend to make requests to the API without running into CORS issues during development and testing
if (typeof window !== "undefined") {
  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    if (args[0].url.includes("https://api.authorix.com")) {
      const replacedUrl = args[0].url.replace("https://api.authorix.com", "https://backend.authorix.com");
      args = [
        new Request(replacedUrl, {
          method: args[0].method,
          headers: args[0].headers,
          body: args[0].body,
          mode: args[0].mode,
          credentials: args[0].credentials,
          cache: args[0].cache,
          redirect: args[0].redirect,
          referrer: args[0].referrer,
          integrity: args[0].integrity,
        }),
        ...args.slice(1),
      ];
    }
    return originalFetch(...args);
  };
}


const config: ZudokuConfig = {
  port: 53312,
  site: {
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Authorix Logo",
    },
  },
  metadata: { favicon: "/favicon.svg" },
  theme: {
    "light": {
      "background": "oklch(100% 0 none)",
      "foreground": "oklch(14.1% 0.004 286)",
      "card": "oklch(100% 0 none)",
      "cardForeground": "oklch(14.1% 0.004 286)",
      "popover": "oklch(100% 0 none)",
      "popoverForeground": "oklch(14.1% 0.004 286)",
      "primary": "oklch(21% 0.006 286)",
      "primaryForeground": "oklch(98.5% 0 none)",
      "secondary": "oklch(96.8% 0.001 286)",
      "secondaryForeground": "oklch(21% 0.006 286)",
      "muted": "oklch(96.8% 0.001 286)",
      "mutedForeground": "oklch(55.2% 0.014 286)",
      "accent": "oklch(96.8% 0.001 286)",
      "accentForeground": "oklch(21% 0.006 286)",
      "destructive": "oklch(63.7% 0.208 25.3)",
      "destructiveForeground": "oklch(98.5% 0 none)",
      "border": "oklch(92% 0.004 286)",
      "input": "oklch(92% 0.004 286)",
      "ring": "oklch(21% 0.006 286)",
      "radius": "0.6rem"
    },
    "dark": {
      "background": "oklch(14.1% 0.004 286)",
      "foreground": "oklch(98.5% 0 none)",
      "card": "oklch(14.1% 0.004 286)",
      "cardForeground": "oklch(98.5% 0 none)",
      "popover": "oklch(14.1% 0.004 286)",
      "popoverForeground": "oklch(98.5% 0 none)",
      "primary": "oklch(98.5% 0 none)",
      "primaryForeground": "oklch(21% 0.006 286)",
      "secondary": "oklch(27.4% 0.005 286)",
      "secondaryForeground": "oklch(98.5% 0 none)",
      "muted": "oklch(27.4% 0.005 286)",
      "mutedForeground": "oklch(71.2% 0.013 286)",
      "accent": "oklch(27.4% 0.005 286)",
      "accentForeground": "oklch(98.5% 0 none)",
      "destructive": "oklch(39.6% 0.133 25.7)",
      "destructiveForeground": "oklch(98.5% 0 none)",
      "border": "oklch(27.4% 0.005 286)",
      "input": "oklch(27.4% 0.005 286)",
      "ring": "oklch(87.1% 0.005 286)",
      "radius": "0.6rem"
    }
  },
  navigation: [
    {
      type: "category",
      label: "Documentation",
      items: [
        {
          type: "category",
          label: "Getting Started",
          icon: "sparkles",
          items: [
            "/introduction",
            "/authentication",
            "/errors",
            "/rate-limits",
            "/balance",
            {
              type: "link",
              icon: "folder-cog",
              label: "API Reference",
              to: "/apis",
            },
          ],
        },
      ],
    },
    {
      type: "link",
      to: "/apis",
      label: "API Reference",
    },
    {
      type: "link",
      to: "https://authorix.com/playground",
      label: "Playground",
      target: "_blank",
    },
  ],
  redirects: [{ from: "/", to: "/introduction" }],
  apis: [
    {
      type: "file",
      input: "./apis/api-schema.yml",
      path: "/apis",
      options: {
        disablePlayground: false,
        examplesLanguage: "python",
        supportedLanguages: [
          { value: "python", label: "Python" },
          { value: "js", label: "JavaScript" },
          { value: "shell", label: "cURL" },
          { value: "java", label: "Java" },
          { value: "go", label: "Go" },
          { value: "csharp", label: "C#" },
          { value: "kotlin", label: "Kotlin" },
          { value: "objc", label: "Objective-C" },
          { value: "php", label: "PHP" },
          { value: "ruby", label: "Ruby" },
          { value: "swift", label: "Swift" },
        ],
        generateCodeSnippet: ({ selectedLang, selectedServer, operation, example }) => {
          return getCodeSnippet({ selectedLang, selectedServer, operation, example })
        },
      }
    },
  ],
  docs: {
    files: "./pages/*.mdx",
    publishMarkdown: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
      includeProtected: false,
    },
  },
};

export default config;
